// Agendas et rendez-vous (H3-08) : plages hebdomadaires, durée de créneau, capacité, fermetures,
// délai d'annulation ; réservation par l'usager, rappel la veille, constat du service. Heures en
// heure de Libreville (UTC+1, sans heure d'été). Calcul des créneaux partagé avec les interfaces.
import { z } from 'zod';
import { schemaDateHeure } from './commun';
import { schemaCodeCollectivite } from './usagers-comptes';

const uuid = z.string().uuid('Identifiant invalide');
const dateCivile = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format AAAA-MM-JJ');
const heure = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure au format HH:MM');

export const DECALAGE_LIBREVILLE = '+01:00';

export const schemaPlageAgenda = z
  .strictObject({ jour: z.number().int().min(1).max(7), debut: heure, fin: heure })
  .refine((p) => p.fin > p.debut, { message: 'La fin de la plage suit son début', path: ['fin'] });
export const schemaFermetureAgenda = z
  .strictObject({ debut: dateCivile, fin: dateCivile, motif: z.string().trim().min(2).max(300) })
  .refine((f) => f.fin >= f.debut, { message: 'La fin de la fermeture suit son début', path: ['fin'] });

export const schemaCreationAgenda = z.strictObject({
  service_id: uuid,
  libelle: z.string().trim().min(2).max(200),
  lieu: z.string().trim().max(300).optional(),
  description: z.string().trim().max(2000).optional(),
  plages: z.array(schemaPlageAgenda).min(1).max(50),
  fermetures: z.array(schemaFermetureAgenda).max(100).default([]),
  duree_creneau_min: z.number().int().min(5).max(480),
  capacite: z.number().int().min(1).max(100),
  delai_annulation_heures: z.number().int().min(0).max(720).default(24),
});
export type Agenda = z.infer<typeof schemaCreationAgenda> & { id: string };

export const schemaSemaine = z.object({ semaine: dateCivile.optional() });
export const schemaParametresCreneaux = z.object({ code: schemaCodeCollectivite, id: uuid });

export const schemaReservation = z.strictObject({
  collectivite: schemaCodeCollectivite,
  agenda_id: uuid,
  debut: schemaDateHeure,
  objet: z.string().trim().max(500).optional(),
  dossier_id: uuid.optional(),
});

export const schemaFiltreRendezVous = z.object({ service: uuid.optional(), date: dateCivile.optional(), agenda: uuid.optional() });

export const schemaEtatRendezVous = z
  .strictObject({
    etat: z.enum(['present', 'absent', 'reporte']),
    nouveau_debut: schemaDateHeure.optional(),
    motif: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.etat !== 'reporte' || v.nouveau_debut, { message: 'Nouveau créneau obligatoire pour un report', path: ['nouveau_debut'] });

/** Lundi de la semaine contenant la date (norme ISO). */
export function lundiDeLaSemaine(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const jour = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - (jour - 1));
  return d.toISOString().slice(0, 10);
}

/** Instant d'une date et d'une heure de Libreville. */
export function instantLibreville(date: string, heureMinute: string): Date {
  return new Date(`${date}T${heureMinute}:00${DECALAGE_LIBREVILLE}`);
}

export interface Creneau {
  debut: string;
  fin: string;
  places_restantes: number;
}

/**
 * Créneaux d'une semaine : plages du jour découpées selon la durée, hors fermetures et hors créneaux
 * passés ; places restantes = capacité moins les réservations actives connues pour ce début.
 */
export function genererCreneaux(
  agenda: Pick<Agenda, 'plages' | 'fermetures' | 'duree_creneau_min' | 'capacite'>,
  lundi: string,
  occupees: ReadonlyMap<string, number>,
  maintenant: Date,
): Creneau[] {
  const creneaux: Creneau[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(`${lundi}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + i);
    const date = d.toISOString().slice(0, 10);
    if (agenda.fermetures.some((f) => date >= f.debut && date <= f.fin)) continue;
    const plages = agenda.plages.filter((p) => p.jour === i + 1).sort((a, b) => a.debut.localeCompare(b.debut));
    for (const p of plages) {
      const finPlage = instantLibreville(date, p.fin).getTime();
      for (let t = instantLibreville(date, p.debut).getTime(); t + agenda.duree_creneau_min * 60_000 <= finPlage; t += agenda.duree_creneau_min * 60_000) {
        if (t <= maintenant.getTime()) continue;
        const debut = new Date(t).toISOString();
        creneaux.push({ debut, fin: new Date(t + agenda.duree_creneau_min * 60_000).toISOString(), places_restantes: Math.max(0, agenda.capacite - (occupees.get(debut) ?? 0)) });
      }
    }
  }
  return creneaux;
}
